export interface Department {
  code: string;
  name: string;
  status?: string[];
}

export interface ApplicationPrivilege {
  code: string;
  name: string;
}

export interface ResourcePrivilegeContext {
  departments: Department[];
}

export interface ResourcePrivilege {
  operationName: string;
  context: ResourcePrivilegeContext;
}

export interface UserPrivilegesResponse {
  applicationPrivileges: ApplicationPrivilege[];
  resourcePrivileges: ResourcePrivilege[];
}

export class UsersSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<any>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Get the current user's privileges for a given application
   * @param {string} accessToken - Bearer token
   * @returns {Promise<UserPrivilegesResponse>} - User privileges
   */
  async getCurrentUserPrivileges(accessToken: string): Promise<UserPrivilegesResponse> {
    const endpoint = `/users/me/privileges`;
    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    return response.json();
  }
}
